import classnames from 'classnames';
import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar/dist/entry.nostyle';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { DEFAULT_LOCALE } from '../../translations/i18n';
import { DayTile } from './MonthlySchedule/DayTile';
import { getScheduleItemByDate, hasDaySchedule } from './MonthlySchedule/utils';
import { OverviewInformation } from './OverviewInformation';

import type { MyScheduleMonthlyProps } from './types';

export const MyScheduleMonthly: React.FC<MyScheduleMonthlyProps> = ({
  initiateCreationOfNewRequest,
  overviewStats,
  selectedDate,
  scheduleItems,
  scheduleElements,
}) => {
  const { t } = useTranslation();
  const { timezone, locale } = useAuth();

  const [activeDate, setActiveDate] = useState<Date | null>(null);

  useEffect(() => {
    setActiveDate(null);
  }, [selectedDate]);

  const handleOnClickDay = (date: Date) => {
    if (!hasDaySchedule(getScheduleItemByDate(date, scheduleItems, timezone))) {
      return;
    }

    setActiveDate((currentActiveDate) => (currentActiveDate && isSameDay(date, currentActiveDate) ? null : date));
  };

  const handleClose = () => setActiveDate(null);

  return (
    <>
      <OverviewInformation {...overviewStats} t={t} testIdPrefix="my-schedule:monthly-view" />
      <Calendar
        locale={locale}
        calendarType={locale === DEFAULT_LOCALE ? 'US' : 'ISO 8601'} // TODO: Extend after adding support for Arabic language
        className="monthly-calendar"
        maxDetail="month"
        minDetail="month"
        onClickDay={handleOnClickDay}
        showNavigation={false}
        tileClassName={({ date }) =>
          classnames('monthly-calendar__day', {
            'monthly-calendar__day--active': activeDate && isSameDay(date, activeDate),
            'monthly-calendar__day--disabled': !hasDaySchedule(getScheduleItemByDate(date, scheduleItems, timezone)),
          })
        }
        tileContent={({ date }) => (
          <DayTile
            activeDate={activeDate}
            date={date}
            initiateCreationOfNewRequest={initiateCreationOfNewRequest}
            scheduleItem={getScheduleItemByDate(date, scheduleItems, timezone)}
            scheduleElements={scheduleElements}
            timezone={timezone}
            onClose={handleClose}
          />
        )}
        value={utcToZonedTime(selectedDate, timezone)}
        view="month"
      />
    </>
  );
};
